import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import type { CategoryResponse, CategoryType } from '@clair-avenir/shared';
import { Button } from '@ds/components/core/Button';
import { Card, CardBody, CardHeader } from '@ds/components/core/Card';
import { Icon } from '@ds/components/core/Icon';
import { Switch } from '@ds/components/forms/Switch';
import { ArchiveConfirmDialog } from '@/components/ArchiveConfirmDialog';
import { AuthenticatedShell, Page, SectionHead } from '@/components/AppShell';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { useToast } from '@/components/ToastProvider';
import { useArchiveCategory, useCategories, useDeleteCategory, useUnarchiveCategory } from '@/features/categories/api';
import { CategoryFormDialog } from '@/features/categories/components/CategoryFormDialog';
import { CategoryRow } from '@/features/categories/components/CategoryRow';
import { ApiError } from '@/lib/http';

export const Route = createFileRoute('/_authenticated/categorias')({ component: CategoriesPage });

const TYPE_SECTIONS: { type: CategoryType; title: string }[] = [
  { type: 'despesa', title: 'Despesas' },
  { type: 'receita', title: 'Receitas' },
  { type: 'transferencia', title: 'Transferências' },
];

interface ArchiveTarget {
  id: string;
  label: string;
  isSystem: boolean;
}

function CategoriesPage() {
  const toast = useToast();
  const [showArchived, setShowArchived] = useState(false);
  const { data: categoriesData } = useCategories(showArchived);

  const [categoryDialog, setCategoryDialog] = useState<{ open: boolean; category?: CategoryResponse }>({ open: false });
  const [archiveTarget, setArchiveTarget] = useState<ArchiveTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null);

  const archiveCategory = useArchiveCategory();
  const unarchiveCategory = useUnarchiveCategory();

  const onDeleteError = (error: unknown) => {
    if (error instanceof ApiError) toast({ tone: 'negative', title: 'Não foi possível excluir', description: error.message });
  };
  const deleteCategory = useDeleteCategory({ onError: onDeleteError });

  function handleArchiveToggle(category: CategoryResponse) {
    if (category.archivedAt) {
      unarchiveCategory.mutate(category.id);
    } else {
      setArchiveTarget({ id: category.id, label: category.name, isSystem: category.isSystem });
    }
  }

  function confirmArchive() {
    if (!archiveTarget) return;
    archiveCategory.mutate(archiveTarget.id, { onSuccess: () => setArchiveTarget(null) });
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    deleteCategory.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
  }

  const categories = categoriesData ?? [];

  return (
    <AuthenticatedShell title="Categorias">
      <Page>
        <div data-testid="categories-page">
          <SectionHead
            title="Categorias"
            action={
              <Button size="sm" iconLeft={<Icon name="plus" />} data-testid="categories-add" onClick={() => setCategoryDialog({ open: true })}>
                Adicionar categoria
              </Button>
            }
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <Switch
              label="Mostrar arquivadas"
              data-testid="categories-show-archived"
              checked={showArchived}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowArchived(e.target.checked)}
            />
          </div>

          <div style={{ display: 'grid', gap: 16 }}>
            {TYPE_SECTIONS.map(({ type, title }) => {
              const items = categories.filter((c) => c.type === type);
              return (
                <Card key={type}>
                  <CardHeader title={title} />
                  <CardBody>
                    {items.length === 0 ? (
                      <p style={{ font: 'var(--type-body)', color: 'var(--text-muted)', margin: 0 }}>Nenhuma categoria deste tipo ainda.</p>
                    ) : (
                      <div>
                        {items.map((category) => (
                          <CategoryRow
                            key={category.id}
                            category={category}
                            onEdit={() => setCategoryDialog({ open: true, category })}
                            onArchiveToggle={() => handleArchiveToggle(category)}
                            onDelete={() => setDeleteTarget({ id: category.id, label: category.name })}
                          />
                        ))}
                      </div>
                    )}
                  </CardBody>
                </Card>
              );
            })}
          </div>
        </div>

        <CategoryFormDialog open={categoryDialog.open} onClose={() => setCategoryDialog({ open: false })} category={categoryDialog.category} />
        <ArchiveConfirmDialog
          open={!!archiveTarget}
          entityLabel={archiveTarget?.label ?? ''}
          verb={archiveTarget?.isSystem ? 'Esconder' : 'Arquivar'}
          reversibleHint={archiveTarget?.isSystem ? 'Você pode reexibir quando quiser.' : 'Você pode reativar quando quiser.'}
          loading={archiveCategory.isPending}
          onClose={() => setArchiveTarget(null)}
          onConfirm={confirmArchive}
        />
        <DeleteConfirmDialog
          open={!!deleteTarget}
          entityLabel={deleteTarget?.label ?? ''}
          loading={deleteCategory.isPending}
          onClose={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      </Page>
    </AuthenticatedShell>
  );
}
